import React, { useState, useMemo, useRef, useCallback, useEffect } from "react";
import { useParams } from "react-router-dom";
import { get } from "../utils/api";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { mapToPdfData } from "../utils/pdfDataMapper";
import fuelifeLogo from "../assets/img/fuelife-logo.jpeg";
import amazingHolidaysLogo from "../assets/img/amazing-holidays-logo.jpeg";
import "./ItineraryPreview.css";

/* ─── helper: compute summary counts ─── */
function computeSummary(days) {
  let flights = 0,
    transfers = 0,
    hotels = 0,
    activities = 0,
    meals = 0;
  for (const day of days) {
    for (const e of day.entries) {
      if (e.type === "flight") flights++;
      else if (e.type === "transfer") transfers++;
      else if (e.type === "meal") meals++;
      else if (e.type === "hotel") {
        hotels++;
        if (e.mealPlan) {
          const mp = e.mealPlan.toLowerCase();
          if (mp.includes("all")) meals += 3;
          else {
            if (mp.includes("breakfast")) meals++;
            if (mp.includes("lunch")) meals++;
            if (mp.includes("dinner")) meals++;
          }
        }
      } else activities++;
    }
  }
  return { flights, transfers, hotels, activities, meals };
}

function computeDaySummary(day) {
  let flights = 0, transfers = 0, hotels = 0, activities = 0, meals = 0;
  for (const e of day.entries || []) {
    if (e.type === "flight") flights++;
    else if (e.type === "transfer") transfers++;
    else if (e.type === "hotel") hotels++;
    else if (e.type === "meal") meals++;
    else activities++;
  }
  return { flights, transfers, hotels, activities, meals };
}

/* ─── icon SVGs ─── */
const FlightIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.2-1.1.5l-1.3 1.5c-.2.3-.1.7.3.8l5.8 2.3-2.6 2.6-2.5-.6c-.4-.1-.8.2-1 .5L1 16.2c-.2.3 0 .7.4.8l3.6 1.1 1.1 3.6c.1.4.5.6.8.4l2.4-1c.3-.2.6-.6.5-1l-.6-2.5 2.6-2.6 2.3 5.8c.1.4.5.5.8.3l1.5-1.3c.3-.2.6-.6.5-1.1z" />
  </svg>
);

const HotelIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 4v16" />
    <path d="M2 8h18a2 2 0 0 1 2 2v10" />
    <path d="M2 17h20" />
    <path d="M6 8v9" />
  </svg>
);

const ActivityIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const TransferIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 16H9m10 0h3v-3.15a1 1 0 0 0-.84-.99L16 11l-2.7-3.6a2 2 0 0 0-1.6-.8H8a2 2 0 0 0-1.6.8L3.7 11l-1.16.86A1 1 0 0 0 2 12.85V16h3" />
    <circle cx="6.5" cy="16.5" r="2.5" />
    <circle cx="16.5" cy="16.5" r="2.5" />
  </svg>
);

const ClockIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const StarIcon = ({ filled }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill={filled ? "#f59e0b" : "none"} stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

const MealIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8h1a4 4 0 0 1 0 8h-1" />
    <path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z" />
    <line x1="6" x2="6" y1="1" y2="4" />
    <line x1="10" x2="10" y1="1" y2="4" />
    <line x1="14" x2="14" y1="1" y2="4" />
  </svg>
);

const LuggageIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 20h0a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h0" />
    <path d="M8 18V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v14" />
    <path d="M10 20h4" />
  </svg>
);

const ChevronDownIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const ChevronUpIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m18 15-6-6-6 6" />
  </svg>
);

const PlaneRightIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14" />
    <path d="m12 5 7 7-7 7" />
  </svg>
);

const TYPE_CONFIG = {
  flight: {
    color: "#7C3AED",
    bgColor: "#EDE9FE",
    label: "FLIGHT",
    Icon: FlightIcon,
  },
  hotel: {
    color: "#D97706",
    bgColor: "#FEF3C7",
    label: "HOTEL",
    Icon: HotelIcon,
  },
  activity: {
    color: "#2563EB",
    bgColor: "#DBEAFE",
    label: "ACTIVITY",
    Icon: ActivityIcon,
  },
  transfer: {
    color: "#16A34A",
    bgColor: "#DCFCE7",
    label: "TRANSFER",
    Icon: TransferIcon,
  },
  meal: {
    color: "#EA580C",
    bgColor: "#FFEDD5",
    label: "MEAL",
    Icon: MealIcon,
  },
};

/* ─── Compact Card Component (Flight / Transfer) ─── */
const CompactCard = ({ entry, isLast }) => {
  const config = TYPE_CONFIG[entry.type] || TYPE_CONFIG.flight;

  return (
    <div className={`ip-entry ${isLast ? "ip-entry--last" : ""}`}>
      <div className="ip-entry__timeline">
        <div className="ip-entry__icon" style={{ borderColor: config.color, color: config.color }}>
          <config.Icon />
        </div>
        {!isLast && <div className="ip-entry__line" />}
      </div>

      <div className="ip-entry__card ip-entry__card--flight">
        <div className="ip-flight__header" style={{ cursor: "default" }}>
          <div className="ip-flight__header-left">
            <span className="ip-entry__tag" style={{ color: config.color, backgroundColor: config.bgColor }}>
              <span className="ip-badge-text">{config.label}</span>
            </span>
            <span className="ip-flight__route">
              • {entry.title}{entry.subtitle ? ` • ${entry.subtitle}` : ""}
            </span>
          </div>
          {entry.time && (
            <span className="ip-entry__time" style={{ marginLeft: "auto", marginRight: "0" }}>
              <ClockIcon />{entry.time}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

/* ─── Entry Card Component ─── */
const EntryCard = ({ entry, isLast }) => {
  const config = TYPE_CONFIG[entry.type] || TYPE_CONFIG.activity;
  const { Icon } = config;
  const [expanded, setExpanded] = useState(true);

  return (
    <div className={`ip-entry ${isLast ? "ip-entry--last" : ""}`}>
      {/* Timeline connector */}
      <div className="ip-entry__timeline">
        <div className="ip-entry__icon" style={{ borderColor: config.color, color: config.color }}>
          <Icon />
        </div>
        {!isLast && <div className="ip-entry__line" />}
      </div>

      {/* Card */}
      <div className="ip-entry__card">
        {/* Card header row */}
        <div
          className="ip-entry__card-header"
          onClick={() => setExpanded(!expanded)}
          style={{ cursor: "pointer" }}
        >
          <div className="ip-entry__card-header-left">
            <span className="ip-entry__tag" style={{ color: config.color, backgroundColor: config.bgColor }}>
              <span className="ip-badge-text">{config.label}</span>
            </span>
            <span className="ip-entry__header-title">
              • {entry.subtitle}
            </span>
          </div>
          {entry.time && (
            <span className="ip-entry__time" style={{ marginLeft: "auto", marginRight: "8px" }}>
              <ClockIcon />{entry.time}
            </span>
          )}
          <div className="ip-entry__card-header-right">
            {expanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
          </div>
        </div>

        {/* Card body with image */}
        {expanded && (
          <div className="ip-entry__card-body">
            {entry.image && (
              <div className="ip-entry__image-wrapper">
                <img src={entry.image} alt={entry.title} className="ip-entry__image" loading="lazy" />
              </div>
            )}
            <div className="ip-entry__details">
              <h4 className="ip-entry__title">{entry.title}</h4>
              <p className="ip-entry__description">{entry.description}</p>

              {/* Hotel-specific extras */}
              {entry.type === "hotel" && (
                <div className="ip-entry__hotel-meta">
                  {entry.rating && (
                    <div className="ip-entry__rating">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <StarIcon key={i} filled={i < entry.rating} />
                      ))}
                    </div>
                  )}
                  {entry.mealPlan && (
                    <div className="ip-entry__meal">
                      <MealIcon />
                      <span>{entry.mealPlan}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ─── Day Section Component ─── */
const DaySection = ({ day }) => {
  const summary = computeDaySummary(day);

  return (
    <div className="ip-day" id={`day-${day.dayNumber}`}>
      {/* Day header with badge + title + included counts */}
      <div className="ip-day__header">
        <div className="ip-day__header-top">
          <div className="ip-day__badge"><span className="ip-badge-text">Day {day.dayNumber}</span></div>
          <div className="ip-day__header-info">
            <h3 className="ip-day__title">{day.title}</h3>
          </div>
        </div>
        <div className="ip-day__included">
          <span className="ip-day__included-label">INCLUDED :</span>
          {summary.flights > 0 && (
            <span className="ip-day__included-item ip-day__included-item--flight">
              <FlightIcon /> {summary.flights} Flight{summary.flights > 1 ? "s" : ""}
            </span>
          )}
          {summary.hotels > 0 && (
            <span className="ip-day__included-item ip-day__included-item--hotel">
              <HotelIcon /> {summary.hotels} Hotel{summary.hotels > 1 ? "s" : ""}
            </span>
          )}
          {summary.transfers > 0 && (
            <span className="ip-day__included-item ip-day__included-item--transfer">
              <TransferIcon /> {summary.transfers} Transfer{summary.transfers > 1 ? "s" : ""}
            </span>
          )}
          {summary.activities > 0 && (
            <span className="ip-day__included-item ip-day__included-item--activity">
              <ActivityIcon /> {summary.activities} Activit{summary.activities > 1 ? "ies" : "y"}
            </span>
          )}
          {summary.meals > 0 && (
            <span className="ip-day__included-item ip-day__included-item--meal">
              <MealIcon /> {summary.meals} Meal{summary.meals > 1 ? "s" : ""}
            </span>
          )}
        </div>
      </div>

      {/* Entries */}
      <div className="ip-day__entries">
        {day.entries.map((entry, idx) =>
          entry.type === "flight" || entry.type === "transfer" || entry.type === "meal" ? (
            <CompactCard key={idx} entry={entry} isLast={idx === day.entries.length - 1} />
          ) : (
            <EntryCard key={idx} entry={entry} isLast={idx === day.entries.length - 1} />
          )
        )}
      </div>
    </div>
  );
};

/* ─── Hero Banner + Logo Placeholders ─── */
const HeroBanner = ({ packageImage }) => {
  if (!packageImage) return null;
  return (
    <div className="ip-hero">
      <img src={packageImage} alt="" className="ip-hero__img" />
      <img src={fuelifeLogo} alt="Fuelife Explorers" className="ip-logo ip-logo--left" />
      <img src={amazingHolidaysLogo} alt="Amazing Holidays" className="ip-logo ip-logo--right" />
    </div>
  );
};

/* ─── Terms / Inclusions / Exclusions / Learning Outcomes Section ─── */
const TermsSection = ({
  inclusions = [],
  exclusions = [],
  termsAndConditions = "",
  paymentPlan = "",
  cancellationPolicy = "",
  notes = "",
  learningOutcomes = [],
}) => {
  const listRows = [
    inclusions.length && {
      key: "inc",
      label: "Inclusions",
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      ),
      colorClass: "ip-terms__col--green",
      items: inclusions,
    },
    exclusions.length && {
      key: "exc",
      label: "Exclusions",
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      ),
      colorClass: "ip-terms__col--red",
      items: exclusions,
    },
    learningOutcomes.length && {
      key: "lo",
      label: "Learning Outcomes",
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" /><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
        </svg>
      ),
      colorClass: "ip-terms__col--purple",
      items: learningOutcomes,
    },
  ].filter(Boolean);

  const htmlRows = [
    termsAndConditions && {
      key: "tc",
      label: "Terms & Conditions",
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" />
        </svg>
      ),
      colorClass: "ip-terms__col--blue",
      html: termsAndConditions,
    },
    paymentPlan && {
      key: "pp",
      label: "Payment Plan",
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <rect x="1" y="4" width="22" height="16" rx="2" ry="2" /><line x1="1" y1="10" x2="23" y2="10" />
        </svg>
      ),
      colorClass: "ip-terms__col--blue",
      html: paymentPlan,
    },
    cancellationPolicy && {
      key: "cp",
      label: "Cancellation Policy",
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
      ),
      colorClass: "ip-terms__col--red",
      html: cancellationPolicy,
    },
    notes && {
      key: "nt",
      label: "Notes",
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" />
        </svg>
      ),
      colorClass: "ip-terms__col--purple",
      html: notes,
    },
  ].filter(Boolean);

  if (!listRows.length && !htmlRows.length) return null;

  return (
    <div className="ip-terms">
      <div className="ip-terms__grid">
        {listRows.map((row) => (
          <div key={row.key} className={`ip-terms__col ${row.colorClass}`}>
            <div className="ip-terms__col-header">
              <span className="ip-terms__col-icon">{row.icon}</span>
              <span className="ip-terms__col-label">{row.label}</span>
            </div>
            <ul className="ip-terms__list">
              {row.items.map((item, i) => (
                <li key={i} className="ip-terms__item">
                  <span className="ip-terms__item-text">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
        {htmlRows.map((row) => (
          <div key={row.key} className={`ip-terms__col ${row.colorClass}`}>
            <div className="ip-terms__col-header">
              <span className="ip-terms__col-icon">{row.icon}</span>
              <span className="ip-terms__col-label">{row.label}</span>
            </div>
            <div
              className="ip-terms__rich-body"
              dangerouslySetInnerHTML={{ __html: row.html }}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

/* ─── Cost Breakdown Section ─── */
const CostBreakdownSection = ({ costBreakdown }) => {
  if (!costBreakdown) return null;

  const fmtInr = (n) =>
    typeof n === "number" && isFinite(n)
      ? "₹ " + Math.round(n).toLocaleString("en-IN")
      : "—";

  return (
    <div className="ip-cost-breakdown">
      <div className="ip-cost-breakdown__card">
      <div className="ip-cost-breakdown__header">
        <span className="ip-cost-breakdown__header-icon">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <path d="M16 8h-6a2 2 0 0 0 0 4h4a2 2 0 0 1 0 4H8" />
            <line x1="12" y1="6" x2="12" y2="8" />
            <line x1="12" y1="16" x2="12" y2="18" />
          </svg>
        </span>
        <span>Cost Breakdown</span>
      </div>
      <table className="ip-cost-breakdown__table">
        <tbody>
          <tr>
            <td>Land Cost</td>
            <td>{fmtInr(costBreakdown.landCost)}</td>
          </tr>
          {costBreakdown.hasFlight && (
            <tr>
              <td>Flight Cost</td>
              <td>{fmtInr(costBreakdown.flightCost)}</td>
            </tr>
          )}
          <tr className="ip-cost-breakdown__total">
            <td>Total Per Head</td>
            <td>{fmtInr(costBreakdown.total)}</td>
          </tr>
        </tbody>
      </table>
      </div>
    </div>
  );
};

/* ─── Main Component ─── */
const ItineraryPreview = ({ data: dataProp, type: typeProp }) => {
  const { id } = useParams();

  // All useState calls first
  const [fetchedData, setFetchedData] = useState(null);
  const [loading, setLoading] = useState(!dataProp && !!id);
  const [fetchError, setFetchError] = useState(false);
  const [activeDay, setActiveDay] = useState(null);
  const [exporting, setExporting] = useState(false);

  // useRef
  const containerRef = useRef(null);

  // useEffect
  useEffect(() => {
    if (dataProp || !id) return;
    const type = typeProp || "itinerary";
    const endpoint =
      type === "package" ? `/api/public/package/${id}` : `/api/public/trip/${id}`;
    get(endpoint)
      .then((res) => {
        setFetchedData(mapToPdfData(res.data, type));
        setLoading(false);
      })
      .catch(() => {
        setFetchError(true);
        setLoading(false);
      });
  }, [id, dataProp, typeProp]);

  // Data resolution (not a hook)
  const data = dataProp || fetchedData;

  const summary = useMemo(() => (data ? computeSummary(data.days) : null), [data]);

  /* ─── PDF Export ─── */
  const handleExportPDF = useCallback(async () => {
    if (!containerRef.current || exporting) return;
    setExporting(true);

    const container = containerRef.current;
    container.classList.add("ip-pdf-export");

    try {
      // Let the new class apply, then wait for all images to be decoded
      // so html2canvas doesn't snapshot half-loaded images.
      await new Promise((r) =>
        requestAnimationFrame(() => requestAnimationFrame(r))
      );
      await Promise.all(
        Array.from(container.querySelectorAll("img")).map((img) =>
          img.complete && img.naturalWidth > 0
            ? Promise.resolve()
            : new Promise((res) => {
                const done = () => res();
                img.addEventListener("load", done, { once: true });
                img.addEventListener("error", done, { once: true });
                // Safety timeout in case the image never fires either event
                setTimeout(done, 5000);
              })
        )
      );

      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const margin = 8; // mm
      const usableWidth = pdfWidth - margin * 2;
      const usableHeight = pdfHeight - margin * 2;

      // Collect all sections to render: cover + topbar + each day + footer
      const sections = [];

      const cover = container.querySelector(".ip-cover");
      if (cover) sections.push(cover);

      const topbar = container.querySelector(".ip-topbar");
      if (topbar) sections.push(topbar);

      const daySections = container.querySelectorAll(".ip-day");
      daySections.forEach((s) => sections.push(s));

      const terms = container.querySelector(".ip-terms");
      if (terms) sections.push(terms);

      const costBreakdown = container.querySelector(".ip-cost-breakdown");
      if (costBreakdown) sections.push(costBreakdown);

      const footer = container.querySelector(".ip-footer");
      if (footer) sections.push(footer);

      let currentY = margin;

      for (let i = 0; i < sections.length; i++) {
        const section = sections[i];

        const canvas = await html2canvas(section, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          logging: false,
          backgroundColor: "#ffffff",
        });

        const imgWidth = usableWidth;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        const pxPerMm = canvas.height / imgHeight;

        const remaining = () => pdfHeight - margin - currentY;

        // Small section that fits remaining space → place it as-is.
        if (imgHeight <= remaining()) {
          pdf.addImage(
            canvas.toDataURL("image/jpeg", 0.92),
            "JPEG",
            margin,
            currentY,
            imgWidth,
            imgHeight
          );
          currentY += imgHeight + 2;
          continue;
        }

        // Small section that doesn't fit remaining space → start on next page.
        if (imgHeight <= usableHeight) {
          pdf.addPage();
          currentY = margin;
          pdf.addImage(
            canvas.toDataURL("image/jpeg", 0.92),
            "JPEG",
            margin,
            currentY,
            imgWidth,
            imgHeight
          );
          currentY += imgHeight + 2;
          continue;
        }

        // Tall section → slice into the remaining space on current page,
        // then flow onto subsequent pages.
        let srcY = 0;
        while (srcY < canvas.height) {
          const mmAvailable = remaining();
          const srcSliceH = Math.min(
            mmAvailable * pxPerMm,
            canvas.height - srcY
          );

          const sliceCanvas = document.createElement("canvas");
          sliceCanvas.width = canvas.width;
          sliceCanvas.height = srcSliceH;
          sliceCanvas.getContext("2d").drawImage(canvas, 0, -srcY);
          const renderMm = srcSliceH / pxPerMm;

          pdf.addImage(
            sliceCanvas.toDataURL("image/jpeg", 0.92),
            "JPEG",
            margin,
            currentY,
            imgWidth,
            renderMm
          );

          srcY += srcSliceH;
          currentY += renderMm;

          if (srcY < canvas.height) {
            pdf.addPage();
            currentY = margin;
          }
        }
        currentY += 2;
      }

      // Open in new tab & trigger download
      const pdfBlob = pdf.output("blob");
      const pdfUrl = URL.createObjectURL(pdfBlob);
      window.open(pdfUrl, "_blank");

      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = `itinerary_${data.packageName.replace(/\s+/g, "_")}.pdf`;
      link.click();
    } catch (err) {
      console.error("PDF Export Error:", err);
    } finally {
      container.classList.remove("ip-pdf-export");
      setExporting(false);
    }
  }, [exporting, data?.packageName]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-500 text-lg">Loading preview...</p>
      </div>
    );
  }

  if (fetchError || !data) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-500 text-lg">Preview not available.</p>
      </div>
    );
  }

  const scrollToDay = (dayNum) => {
    setActiveDay(dayNum);
    const el = document.getElementById(`day-${dayNum}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <div className="ip-container" ref={containerRef}>

      {/* ── Cover header (trip title, subtitle, meta) ── */}
      <div className="ip-cover">
        <HeroBanner packageImage={data.packageImage} />
        <h1 className="ip-cover__title">{data.packageName}</h1>
        <p className="ip-cover__subtitle">{data.subtitle}</p>
        <div className="ip-cover__meta">
          {data.schoolName && (
            <div className="ip-cover__meta-item">
              <span className="ip-cover__meta-label">School / Group</span>
              <span className="ip-cover__meta-value">{data.schoolName}</span>
            </div>
          )}
          <div className="ip-cover__meta-item">
            <span className="ip-cover__meta-label">Duration</span>
            <span className="ip-cover__meta-value">
              {data.duration.days}D / {data.duration.nights}N
            </span>
          </div>
          <div className="ip-cover__meta-item">
            <span className="ip-cover__meta-label">Travelers</span>
            <span className="ip-cover__meta-value">{data.pax}</span>
          </div>
          <div className="ip-cover__meta-item">
            <span className="ip-cover__meta-label">Dates</span>
            <span className="ip-cover__meta-value">
              {data.startDate} – {data.endDate}
            </span>
          </div>
        </div>
      </div>

      {/* ── Top Summary Bar (matching screenshot) ── */}
      <div className="ip-topbar">
        <div className="ip-topbar__plan-badge">
          <span className="ip-badge-text">{data.duration.days} DAY PLAN</span>
        </div>
        <div className="ip-topbar__stats">
          <span className="ip-topbar__stat">
            <strong>{summary.flights}</strong> FLIGHTS &amp; <strong>{summary.transfers}</strong> TRANSFERS
          </span>
          <span className="ip-topbar__stat">
            <strong>{summary.hotels}</strong> HOTELS
          </span>
          <span className="ip-topbar__stat">
            <strong>{summary.activities}</strong> ACTIVITIES
          </span>
          <span className="ip-topbar__stat">
            <strong>{summary.meals}</strong> MEALS
          </span>
        </div>
      </div>

      {/* ── Day Sections ── */}
      <div className="ip-days">
        {data.days.map((day) => (
          <DaySection key={day.dayNumber} day={day} />
        ))}
      </div>

      {/* ── Inclusions / Exclusions / Payment Plan / Cancellation Policy / Notes / Learning Outcomes ── */}
      <TermsSection
        inclusions={data.inclusions}
        exclusions={data.exclusions}
        termsAndConditions={data.termsAndConditions}
        paymentPlan={data.paymentPlan}
        cancellationPolicy={data.cancellationPolicy}
        notes={data.notes}
        learningOutcomes={data.learningOutcomes}
      />

      {/* ── Cost Breakdown ── */}
      <CostBreakdownSection costBreakdown={data.costBreakdown} />

      {/* ── Footer ── */}
      <div className="ip-footer">
        <p className="ip-footer__note">
          This itinerary is subject to change based on weather conditions and
          local regulations.
        </p>
        <p className="ip-footer__company">Fuelife Explorers Pvt Ltd</p>
        <p className="ip-footer__contact">
          +91 8147771333 &nbsp;|&nbsp; enquiry@fuelifeexplorers.com &nbsp;|&nbsp;
          www.fuelifeexplorers.com
        </p>
      </div>

      {/* ── Floating Export PDF Button ── */}
      {/* <button
        className={`ip-export-btn ${exporting ? "ip-export-btn--loading" : ""}`}
        onClick={handleExportPDF}
        disabled={exporting}
        title="Export as PDF"
      >
        {exporting ? (
          <>
            <span className="ip-export-btn__spinner" />
            Generating…
          </>
        ) : (
          <>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" x2="8" y1="13" y2="13" />
              <line x1="16" x2="8" y1="17" y2="17" />
              <polyline points="10 9 9 9 8 9" />
            </svg>
            Export PDF
          </>
        )}
      </button> */}
    </div>
  );
};

export default ItineraryPreview;
